import React from 'react';
import styled, { keyframes } from 'styled-components';
import Lottie from 'lottie-react';
import Card from '../../Card';
import PrimaryCta from '../../PrimaryCta';
import Astronaut from '../../../assets/Astronaut.json';
import { Ticket, Project, timeAgo } from '../mock';
import { resolveClientDisplayNameFromTicket } from '../../../lib/clientDisplayName';
import InternalAssigneeIdentity from './InternalAssigneeIdentity';
import {
  resolveInternalAssigneeSortValue,
} from '../../../lib/ticketAssigneeDisplay';

const Wrap = styled(Card)`
  padding: 0;
  overflow: hidden;

  /* fixed-height card: top bar + scroll region */
  display: grid;
  grid-template-rows: auto 1fr;
  height: clamp(360px, 60vh, 640px);
`;

/* Top bar: project name + create ticket */
const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid rgba(255,255,255,.08);
  background: rgba(255,255,255,.04);

  @media (max-width: 767px) {
    align-items: flex-start;
    flex-wrap: wrap;
  }
`;
const Title = styled.div`
  font-weight: 900;
  letter-spacing: .3px;
  color: #E8EAED;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;

  @media (max-width: 767px) {
    width: 100%;
    white-space: normal;
    line-height: 1.3;
  }
`;
const CreateBtn = styled.button`
  all: unset;
  padding: 8px 12px;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,.08);
  background: rgba(255,255,255,.06);
  color: #E8EAED;
  font-weight: 800;
  cursor: pointer;
  &:hover { background: rgba(255,255,255,.08); }

  @media (max-width: 767px) {
    width: 100%;
    text-align: center;
  }
`;

/* Scroll area */
const Scroll = styled.div`
  overflow: auto;
  color-scheme: dark;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  touch-action: pan-x pan-y;
  scrollbar-width: thin;
  scrollbar-color: rgba(224, 161, 94, .46) rgba(255,255,255,.06);

  &::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255,255,255,.04);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(224, 161, 94, .42);
    border-radius: 999px;
    border: 2px solid rgba(15, 18, 26, .82);
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(224, 161, 94, .62);
  }
`;

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;


/* Desktop table */
const DesktopOnly = styled.div<{ $mobileMode: 'cards' | 'table' }>`
  display: block;
  @media (max-width: 767px) {
    display: ${({ $mobileMode }) => ($mobileMode === 'table' ? 'block' : 'none')};
  }
`;

const Table = styled.table`
  width: 100%;
  min-width: 2240px;
  border-collapse: separate;
  border-spacing: 0;
  color: #E8EAED;
  font-size: 14px;

  th, td {
    padding: 12px 14px;
    text-align: left;
    white-space: nowrap;
    vertical-align: top;
  }

  thead th {
    position: sticky;
    top: 0;
    z-index: 3;
    font-weight: 900;
    letter-spacing: .3px;
    background: #1f2430;
    border-bottom: 1px solid rgba(255,255,255,.10);
  }

  tbody td { border-bottom: 1px solid rgba(255,255,255,.08); }
  tbody tr:hover td { background: rgba(255,255,255,.03); cursor: pointer; }

  @media (max-width: 767px) {
    min-width: 1560px;
    font-size: 13px;

    th, td {
      padding: 11px 12px;
    }
  }
`;

const SortButton = styled.button<{ $active: boolean }>`
  all: unset;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  color: ${({ $active }) => ($active ? '#E8EAED' : '#cbd2e1')};

  &:hover {
    color: #E8EAED;
  }
`;

const SortIcon = styled.span<{ $active: boolean }>`
  font-size: 11px;
  line-height: 1;
  color: ${({ $active }) => ($active ? '#E8EAED' : '#8b95a7')};
`;

const Status = styled.span<{ $bg: string }>`
  padding: 4px 10px; border-radius: 999px; font-size: 12px; font-weight: 800;
  color: #111827; background: ${({ $bg }) => $bg};
`;
const Dot = styled.span<{ $color: string }>`
  display: inline-block; width: 8px; height: 8px; border-radius: 50%;
  background: ${({ $color }) => $color};
`;

/* Mobile list */
const MobileOnly = styled.div<{ $mobileMode: 'cards' | 'table' }>`
  display: none;
  @media (max-width: 767px) {
    display: ${({ $mobileMode }) => ($mobileMode === 'cards' ? 'block' : 'none')};
  }
`;

const MobileList = styled.div`
  display: grid;
`;

const RowCard = styled.button`
  all: unset;
  width: 100%;
  display: grid;
  gap: 6px;
  padding: 12px 14px;
  border-bottom: 1px solid rgba(255,255,255,.08);
  cursor: pointer;
  &:active { background: rgba(255,255,255,.04); }
`;
const RowTitle = styled.div`
  font-weight: 800; color: #E8EAED;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`;
const RowMeta = styled.div`
  display: flex; flex-wrap: wrap; align-items: center; gap: 10px;
  color: #cbd2e1; font-size: 12px; font-weight: 700;
`;
const Pill = styled.span`
  display: inline-grid; grid-auto-flow: column; align-items: center; gap: 8px;
  padding: 4px 8px; border-radius: 999px;
  border: 1px solid rgba(255,255,255,.08);
  background: rgba(255,255,255,.04);
`;

const SkeletonBar = styled.div<{ $width?: string; $height?: string; $rounded?: string }>`
  width: ${({ $width }) => $width || '100%'};
  height: ${({ $height }) => $height || '14px'};
  border-radius: ${({ $rounded }) => $rounded || '8px'};
  background: linear-gradient(
    90deg,
    rgba(255,255,255,.06) 0%,
    rgba(255,255,255,.12) 50%,
    rgba(255,255,255,.06) 100%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.35s linear infinite;
`;

const SkeletonPill = styled(SkeletonBar)`
  height: 24px;
  border-radius: 999px;
`;

const SkeletonCellStack = styled.div`
  display: grid;
  gap: 8px;
`;

/* Empty state with Lottie background */
const EmptyState = styled.div`
  position: relative;
  height: 100%;
  display: grid;
  place-items: center;
`;
const LottieBg = styled(Lottie)`
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  opacity: .85; pointer-events: none;
  filter: saturate(.9) brightness(.95);
`;
const EmptyOverlay = styled.div`
  position: relative;
  display: grid; gap: 12px;
  text-align: center;
  padding: 16px; max-width: 420px;
  color: #E8EAED;

  h3 { margin: 0; font-size: 18px; font-weight: 900; letter-spacing: .3px; text-transform: uppercase; }
  p  { margin: 0; color: #cbd2e1; }
`;

/* helpers */
function toneFor(status: Ticket['status']) {
  switch (status) {
    case 'in_progress': return '#A7F3D0';
    case 'blocked': return '#FDE68A';
    case 'delivered': return '#93C5FD';
    case 'submitted': return '#E5E7EB';
    case 'triage': return '#FCA5A5';
    case 'closed': return '#D1D5DB';
    default: return '#E5E7EB';
  }
}
function priorityDot(p: Ticket['priority']) {
  return p === 'urgent' ? '#EF4444' : p === 'high' ? '#F59E0B' : p === 'medium' ? '#22D3EE' : '#9CA3AF';
}

type Props = {
  tickets: Ticket[];
  projects: Project[];
  onOpen: (id: string) => void;
  /** current selected project id; 'all' shows All tickets; controls header text */
  projectId?: string | 'all';
  title?: string;
  /** opens the create drawer for this project (shown when a specific project is selected) */
  onCreate?: () => void;
  /** explicit panel height override */
  height?: string;
  loading?: boolean;
  mobileMode?: 'cards' | 'table';
};

type SortKey = 'ticket_id' | 'title' | 'status' | 'priority' | 'project' | 'milestone' | 'client' | 'offering' | 'subcategory' | 'team' | 'assigned' | 'updated';
type SortDirection = 'asc' | 'desc';

const SORT_COLUMNS: ReadonlyArray<{ key: SortKey; label: string }> = [
  { key: 'ticket_id', label: 'Ticket ID' },
  { key: 'title', label: 'Title' },
  { key: 'status', label: 'Status' },
  { key: 'priority', label: 'Priority' },
  { key: 'project', label: 'Project' },
  { key: 'milestone', label: 'Milestone' },
  { key: 'client', label: 'Client' },
  { key: 'offering', label: 'Offering' },
  { key: 'subcategory', label: 'Subcategory' },
  { key: 'team', label: 'Team' },
  { key: 'assigned', label: 'Assigned' },
  { key: 'updated', label: 'Updated' },
];

const COLUMN_WIDTHS: Record<SortKey, string> = {
  ticket_id: '118px',
  title: '320px',
  status: '132px',
  priority: '114px',
  project: '250px',
  milestone: '250px',
  client: '170px',
  offering: '220px',
  subcategory: '250px',
  team: '180px',
  assigned: '180px',
  updated: '118px',
};

const STATUS_ORDER: Record<Ticket['status'], number> = {
  submitted: 0,
  triage: 1,
  in_progress: 2,
  blocked: 3,
  delivered: 4,
  closed: 5,
};

const PRIORITY_ORDER: Record<Ticket['priority'], number> = {
  low: 0,
  medium: 1,
  high: 2,
  urgent: 3,
};

function norm(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

function safeStamp(value: unknown): number {
  if (!value) return 0;
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? parsed : 0;
}

function renderTicketHeading(ticket: Ticket) {
  const title =
    ticket.title && ticket.title.trim()
      ? ticket.title
      : <em style={{ color: '#9aa4b2' }}>Untitled ticket</em>;
  return title;
}

function renderTicketId(ticket: Ticket) {
  const ticketNumber = String((ticket as any).ticketNumber ?? '').trim();
  if (!ticketNumber) return '--';
  return <span style={{ color: '#8DEBFF', fontWeight: 900 }}>#{ticketNumber}</span>;
}

function resolveMilestoneLabel(ticket: Ticket) {
  const source = ticket as any;
  const raw =
    source.milestoneTitle
    ?? source.milestoneName
    ?? source.milestoneLabel
    ?? source.milestone;

  if (typeof raw === 'string') {
    const value = raw.trim();
    return value || '--';
  }

  if (raw && typeof raw === 'object') {
    const nested = raw.title ?? raw.name ?? raw.label;
    const value = typeof nested === 'string' ? nested.trim() : '';
    return value || '--';
  }

  return '--';
}

function resolveTeamLabel(ticket: Ticket) {
  const values = [
    (ticket as any)?.currentTeamLabel,
    (ticket as any)?.defaultTeamLabel,
    (ticket as any)?.currentTeamNodeId,
    (ticket as any)?.owningTeamNodeId,
    (ticket as any)?.teamId,
    (ticket as any)?.defaultTeamNodeId,
  ];

  for (const value of values) {
    if (typeof value !== 'string') continue;
    const normalized = value.trim();
    if (normalized) return normalized;
  }

  return '--';
}

function resolveOfferingLabel(ticket: Ticket) {
  const values = [
    (ticket as any)?.offeringName,
    (ticket as any)?.offeringCode,
  ];

  for (const value of values) {
    if (typeof value !== 'string') continue;
    const normalized = value.trim();
    if (normalized) return normalized;
  }

  return '--';
}

function resolveSubcategoryLabel(ticket: Ticket) {
  const values = [
    (ticket as any)?.offeringSubcategoryName,
    (ticket as any)?.offeringSubcategoryId,
  ];

  for (const value of values) {
    if (typeof value !== 'string') continue;
    const normalized = value.trim();
    if (normalized) return normalized;
  }

  return '--';
}

function columnWidthStyle(key: SortKey): React.CSSProperties {
  return {
    minWidth: COLUMN_WIDTHS[key],
  };
}

export default function TicketsTable({
  tickets,
  projects,
  onOpen,
  projectId,
  title,
  onCreate,
  height,
  loading = false,
  mobileMode = 'cards',
}: Props) {
  const byId = React.useMemo(() => new Map(projects.map(p => [p.id, p])), [projects]);
  const [sortState, setSortState] = React.useState<{ key: SortKey; direction: SortDirection } | null>(null);

  const toggleSort = React.useCallback((key: SortKey) => {
    setSortState((prev) => {
      if (!prev || prev.key !== key) return { key, direction: 'asc' };
      return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
    });
  }, []);

  const sortedTickets = React.useMemo(() => {
    if (!sortState) return tickets;

    const compare = (a: Ticket, b: Ticket) => {
      switch (sortState.key) {
        case 'ticket_id': {
          const aNumber = Number(String((a as any).ticketNumber ?? '').trim());
          const bNumber = Number(String((b as any).ticketNumber ?? '').trim());
          const aSafe = Number.isFinite(aNumber) ? aNumber : 0;
          const bSafe = Number.isFinite(bNumber) ? bNumber : 0;
          if (aSafe !== bSafe) return aSafe - bSafe;
          return norm(a.title || '').localeCompare(norm(b.title || ''));
        }
        case 'title': {
          return norm(a.title || 'Untitled ticket').localeCompare(norm(b.title || 'Untitled ticket'));
        }
        case 'status': {
          const delta = (STATUS_ORDER[a.status] ?? 999) - (STATUS_ORDER[b.status] ?? 999);
          if (delta !== 0) return delta;
          return norm(a.title).localeCompare(norm(b.title));
        }
        case 'priority': {
          const delta = (PRIORITY_ORDER[a.priority] ?? 999) - (PRIORITY_ORDER[b.priority] ?? 999);
          if (delta !== 0) return delta;
          return norm(a.title).localeCompare(norm(b.title));
        }
        case 'project': {
          const aProject = norm(byId.get(a.projectId)?.name ?? '');
          const bProject = norm(byId.get(b.projectId)?.name ?? '');
          return aProject.localeCompare(bProject);
        }
        case 'milestone': {
          const aMilestone = norm(resolveMilestoneLabel(a));
          const bMilestone = norm(resolveMilestoneLabel(b));
          return aMilestone.localeCompare(bMilestone);
        }
        case 'client': {
          const aClient = norm(resolveClientDisplayNameFromTicket(a, ''));
          const bClient = norm(resolveClientDisplayNameFromTicket(b, ''));
          return aClient.localeCompare(bClient);
        }
        case 'offering': {
          const aOffering = norm(resolveOfferingLabel(a));
          const bOffering = norm(resolveOfferingLabel(b));
          return aOffering.localeCompare(bOffering);
        }
        case 'subcategory': {
          const aSubcategory = norm(resolveSubcategoryLabel(a));
          const bSubcategory = norm(resolveSubcategoryLabel(b));
          return aSubcategory.localeCompare(bSubcategory);
        }
        case 'team': {
          const aTeam = norm(resolveTeamLabel(a));
          const bTeam = norm(resolveTeamLabel(b));
          return aTeam.localeCompare(bTeam);
        }
        case 'assigned': {
          const aAssigned = resolveInternalAssigneeSortValue(a as any);
          const bAssigned = resolveInternalAssigneeSortValue(b as any);
          return aAssigned.localeCompare(bAssigned);
        }
        case 'updated': {
          return safeStamp(a.updatedAt) - safeStamp(b.updatedAt);
        }
        default:
          return 0;
      }
    };

    const direction = sortState.direction === 'asc' ? 1 : -1;
    return [...tickets].sort((a, b) => compare(a, b) * direction);
  }, [byId, sortState, tickets]);

  const projectLabel =
    !projectId ? 'Tickets' :
      projectId === 'all' ? 'All tickets' :
        (byId.get(projectId)?.name ?? 'Tickets');
  const panelTitle = title || projectLabel;

  const isEmpty = !loading && (!tickets || tickets.length === 0);
  const skeletonRows = React.useMemo(() => Array.from({ length: 7 }, (_, index) => index), []);

  return (
    <Wrap aria-label="Tickets table" style={height ? { height } : undefined}>
      <TopBar>
        <Title>{panelTitle}</Title>
        {projectId && projectId !== 'all' && onCreate && (
          <CreateBtn onClick={onCreate}>Create ticket</CreateBtn>
        )}
      </TopBar>

      <Scroll>
        {loading ? (
          <>
            <DesktopOnly $mobileMode={mobileMode}>
              <Table>
                <thead>
                  <tr>
                    {SORT_COLUMNS.map((column) => (
                      <th key={column.key} style={columnWidthStyle(column.key)}>{column.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {skeletonRows.map((row) => (
                    <tr key={`ticket-skeleton-${row}`}>
                      <td><SkeletonBar $width="92px" /></td>
                      <td>
                        <SkeletonCellStack>
                          <SkeletonBar $width="68%" />
                          <SkeletonBar $width="38%" $height="12px" />
                        </SkeletonCellStack>
                      </td>
                      <td><SkeletonPill $width="96px" /></td>
                      <td><SkeletonBar $width="88px" /></td>
                      <td><SkeletonBar $width="132px" /></td>
                      <td><SkeletonBar $width="148px" /></td>
                      <td><SkeletonBar $width="144px" /></td>
                      <td><SkeletonBar $width="156px" /></td>
                      <td><SkeletonBar $width="176px" /></td>
                      <td><SkeletonBar $width="120px" /></td>
                      <td><SkeletonBar $width="110px" /></td>
                      <td><SkeletonBar $width="84px" /></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </DesktopOnly>

            <MobileOnly $mobileMode={mobileMode}>
              <MobileList>
                {skeletonRows.slice(0, 5).map((row) => (
                  <RowCard as="div" key={`ticket-mobile-skeleton-${row}`}>
                    <RowTitle><SkeletonBar $width="72%" /></RowTitle>
                    <RowMeta>
                      <Pill><SkeletonBar $width="62px" $height="12px" /></Pill>
                      <Pill><SkeletonBar $width="72px" $height="12px" /></Pill>
                      <Pill><SkeletonBar $width="84px" $height="12px" /></Pill>
                    </RowMeta>
                  </RowCard>
                ))}
              </MobileList>
            </MobileOnly>
          </>
        ) : isEmpty ? (
          <EmptyState>
            <LottieBg animationData={Astronaut} loop autoplay />
            <EmptyOverlay>
              <h3>Create your first ticket</h3>
              <p>Kick things off by submitting a request for this project.</p>
              {onCreate && projectId && projectId !== 'all' && (
                <PrimaryCta onClick={onCreate}>Create ticket</PrimaryCta>
              )}
            </EmptyOverlay>
          </EmptyState>
        ) : (
          <>
            {/* Desktop table */}
            <DesktopOnly $mobileMode={mobileMode}>
              <Table>
                <thead>
                  <tr>
                    {SORT_COLUMNS.map((column) => {
                      const active = sortState?.key === column.key;
                      const icon = active
                        ? (sortState?.direction === 'asc' ? '^' : 'v')
                        : '<>';

                      return (
                        <th key={column.key}>
                          <div style={columnWidthStyle(column.key)}>
                          <SortButton
                            type="button"
                            $active={active}
                            onClick={() => toggleSort(column.key)}
                            aria-label={`Sort by ${column.label}`}
                          >
                            <span>{column.label}</span>
                            <SortIcon $active={active}>{icon}</SortIcon>
                          </SortButton>
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {sortedTickets.map(t => (
                    <tr key={t.id} onClick={() => onOpen(t.id)}>
                      <td style={columnWidthStyle('ticket_id')}>{renderTicketId(t)}</td>
                      <td style={columnWidthStyle('title')}>
                        {t.unread && (
                          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#34D399', marginRight: 8 }}
                          />
                        )}
                        <span style={{ display: 'inline-block', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                          {renderTicketHeading(t)}
                        </span>
                      </td>
                      <td style={columnWidthStyle('status')}><Status $bg={toneFor(t.status)}>{t.status.replace(/_/g, ' ')}</Status></td>
                      <td style={columnWidthStyle('priority')}><Dot $color={priorityDot(t.priority)} /> <span style={{ marginLeft: 8 }}>{t.priority}</span></td>
                      <td style={columnWidthStyle('project')} title={byId.get(t.projectId)?.name ?? '--'}>
                        <span style={{ display: 'inline-block', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                          {byId.get(t.projectId)?.name ?? '--'}
                        </span>
                      </td>
                      <td style={columnWidthStyle('milestone')} title={resolveMilestoneLabel(t)}>
                        <span style={{ display: 'inline-block', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                          {resolveMilestoneLabel(t)}
                        </span>
                      </td>
                      <td style={columnWidthStyle('client')} title={resolveClientDisplayNameFromTicket(t, '--')}>
                        <span style={{ display: 'inline-block', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                          {resolveClientDisplayNameFromTicket(t, '--')}
                        </span>
                      </td>
                      <td style={columnWidthStyle('offering')} title={resolveOfferingLabel(t)}>
                        <span style={{ display: 'inline-block', maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                          {resolveOfferingLabel(t)}
                        </span>
                      </td>
                      <td style={columnWidthStyle('subcategory')} title={resolveSubcategoryLabel(t)}>
                        <span style={{ display: 'inline-block', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                          {resolveSubcategoryLabel(t)}
                        </span>
                      </td>
                      <td style={columnWidthStyle('team')} title={resolveTeamLabel(t)}>
                        <span style={{ display: 'inline-block', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                          {resolveTeamLabel(t)}
                        </span>
                      </td>
                      <td style={columnWidthStyle('assigned')}><InternalAssigneeIdentity source={t as any} fallback="--" /></td>
                      <td style={columnWidthStyle('updated')} title={new Date(t.updatedAt).toLocaleString()}>{timeAgo(t.updatedAt)} ago</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </DesktopOnly>

            {/* Mobile list */}
            <MobileOnly $mobileMode={mobileMode}>
              <MobileList>
                {sortedTickets.map(t => (
                  <RowCard key={t.id} onClick={() => onOpen(t.id)}>
                    <RowTitle>
                      {t.unread && (
                        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#34D399', marginRight: 8 }}
                        />
                      )}
                      {renderTicketHeading(t)}
                    </RowTitle>
                    <RowMeta>
                      <Pill title="Ticket ID">{renderTicketId(t)}</Pill>
                      <Status $bg={toneFor(t.status)}>{t.status.replace(/_/g, ' ')}</Status>
                      <span><Dot $color={priorityDot(t.priority)} /> <span style={{ marginLeft: 6 }}>{t.priority}</span></span>
                      <Pill title="Project">{byId.get(t.projectId)?.name ?? '--'}</Pill>
                      <Pill title="Milestone">{resolveMilestoneLabel(t)}</Pill>
                      <Pill title="Client">{resolveClientDisplayNameFromTicket(t, '--')}</Pill>
                      <Pill title="Offering">{resolveOfferingLabel(t)}</Pill>
                      <Pill title="Subcategory">{resolveSubcategoryLabel(t)}</Pill>
                      <Pill title="Team">{resolveTeamLabel(t)}</Pill>
                      <Pill title="Assigned"><InternalAssigneeIdentity source={t as any} size={20} fallback="Unassigned" /></Pill>
                      <span title={new Date(t.updatedAt).toLocaleString()}>{timeAgo(t.updatedAt)} ago</span>
                    </RowMeta>
                  </RowCard>
                ))}
              </MobileList>
            </MobileOnly>
          </>
        )}
      </Scroll>
    </Wrap>
  );
}

